// ERC20 Token contract addresses across chains
// Standard ERC20 ABI for transfer and balanceOf
export const ERC20_ABI = [
  {
    name: 'transfer',
    type: 'function',
    stateMutability: 'nonpayable',
    inputs: [
      { name: 'to', type: 'address' },
      { name: 'amount', type: 'uint256' },
    ],
    outputs: [{ name: '', type: 'bool' }],
  },
  {
    name: 'balanceOf',
    type: 'function',
    stateMutability: 'view',
    inputs: [{ name: 'account', type: 'address' }],
    outputs: [{ name: '', type: 'uint256' }],
  },
  {
    name: 'decimals',
    type: 'function',
    stateMutability: 'view',
    inputs: [],
    outputs: [{ name: '', type: 'uint8' }],
  },
  {
    name: 'symbol',
    type: 'function',
    stateMutability: 'view',
    inputs: [],
    outputs: [{ name: '', type: 'string' }],
  },
] as const;

export interface TokenInfo {
  symbol: string;
  name: string;
  decimals: number;
  icon: string;
  // Chain ID -> contract address
  addresses: Record<number, `0x${string}`>;
}

// Chain IDs: 1=Ethereum, 56=BSC, 137=Polygon, 42161=Arbitrum, 10=Optimism, 8453=Base, 43114=Avalanche

export const ERC20_TOKENS: TokenInfo[] = [
  // ═══════════════════════ Stablecoins ═══════════════════════
  {
    symbol: 'USDT', name: 'Tether USD', decimals: 6, icon: '₮',
    addresses: {
      1: '0xdAC17F958D2ee523a2206206994597C13D831ec7',
      56: '0x55d398326f99059fF775485246999027B3197955',
      137: '0xc2132D05D31c914a87C6611C10748AEb04B58e8F',
      42161: '0xFd086bC7CD5C481DCC9C85ebE478A1C0b69FCbb9',
      10: '0x94b008aA00579c1307B0EF2c499aD98a8ce58e58',
      43114: '0x9702230A8Ea53601f5cD2dc00fDBc13d4dF4A8c7',
    },
  },
  {
    symbol: 'USDC', name: 'USD Coin', decimals: 6, icon: '$',
    addresses: {
      1: '0xA0b86991c6218b36c1d19D4a2e9Eb0cE3606eB48',
      56: '0x8AC76a51cc950d9822D68b83fE1Ad97B32Cd580d',
      137: '0x3c499c542cEF5E3811e1192ce70d8cC03d5c3359',
      42161: '0xaf88d065e77c8cC2239327C5EDb3A432268e5831',
      10: '0x0b2C639c533813f4Aa9D7837CAf62653d097Ff85',
      8453: '0x833589fCD6eDb6E08f4c7C32D4f71b54bdA02913',
      43114: '0xB97EF9Ef8734C71904D8002F8b6Bc66Dd9c48a6E',
    },
  },
  {
    symbol: 'DAI', name: 'Dai Stablecoin', decimals: 18, icon: '◈',
    addresses: {
      1: '0x6B175474E89094C44Da98b954EedeAC495271d0F',
      137: '0x8f3Cf7ad23Cd3CaDbD9735AFf958023239c6A063',
      42161: '0xDA10009cBd5D07dd0CeCc66161FC93D7c9000da1',
      10: '0xDA10009cBd5D07dd0CeCc66161FC93D7c9000da1',
      8453: '0x50c5725949A6F0c72E6C4a641F24049A917DB0Cb',
    },
  },
  {
    symbol: 'BUSD', name: 'Binance USD', decimals: 18, icon: '$',
    addresses: {
      1: '0x4Fabb145d64652a948d72533023f6E7A623C7C53',
      56: '0xe9e7CEA3DedcA5984780Bafc599bD69ADd087D56',
    },
  },
  {
    symbol: 'TUSD', name: 'TrueUSD', decimals: 18, icon: '$',
    addresses: {
      1: '0x0000000000085d4780B73119b644AE5ecd22b376',
      56: '0x40af3827F39D0EAcBF4A168f8D4ee67c121D11c9',
    },
  },
  {
    symbol: 'FRAX', name: 'Frax', decimals: 18, icon: '⨎',
    addresses: {
      1: '0x853d955aCEf822Db058eb8505911ED77F175b99e',
      42161: '0x17FC002b466eEc40DaE837Fc4bE5c67993ddBd6F',
      137: '0x45c32fA6DF82ead1e2EF74d17b76547EDdFaFF89',
    },
  },

  // ═══════════════════════ Major Tokens ═══════════════════════
  {
    symbol: 'WBTC', name: 'Wrapped Bitcoin', decimals: 8, icon: '₿',
    addresses: {
      1: '0x2260FAC5E5542a773Aa44fBCfeDf7C193bc2C599',
      137: '0x1BFD67037B42Cf73acF2047067bd4F2C47D9BfD6',
      42161: '0x2f2a2543B76A4166549F7aaB2e75Bef0aefC5B0f',
      10: '0x68f180fcCe6836688e9084f035309E29Bf0A2095',
      43114: '0x50b7545627a5162F82A992c33b87aDc75187B218',
    },
  },
  {
    symbol: 'WETH', name: 'Wrapped Ether', decimals: 18, icon: 'Ξ',
    addresses: {
      56: '0x2170Ed0880ac9A755fd29B2688956BD959F933F8',
      137: '0x7ceB23fD6bC0adD59E62ac25578270cFf1b9f619',
      42161: '0x82aF49447D8a07e3bd95BD0d56f35241523fBab1',
      10: '0x4200000000000000000000000000000000000006',
      8453: '0x4200000000000000000000000000000000000006',
      43114: '0x49D5c2BdFfac6CE2BFdB6640F4F80f226bc10bAB',
    },
  },
  {
    symbol: 'BNB', name: 'BNB (Wrapped)', decimals: 18, icon: '◈',
    addresses: {
      1: '0xB8c77482e45F1F44dE1745F52C74426C631bDD52',
    },
  },
  {
    symbol: 'LINK', name: 'Chainlink', decimals: 18, icon: '⬡',
    addresses: {
      1: '0x514910771AF9Ca656af840dff83E8264EcF986CA',
      56: '0xF8A0BF9cF54Bb92F17374d9e9A321E6a111a51bD',
      137: '0x53E0bca35eC356BD5ddDFebbD1Fc0fD03FaBad39',
      42161: '0xf97f4df75117a78c1A5a0DBb814Af92458539FB4',
      10: '0x350a791Bfc2C21F9Ed5d10980Dad2e2638ffa7f6',
      43114: '0x5947BB275c521040051D82396571092d30fECDf1',
    },
  },
  {
    symbol: 'UNI', name: 'Uniswap', decimals: 18, icon: '🦄',
    addresses: {
      1: '0x1f9840a85d5aF5bf1D1762F925BDADdC4201F984',
      137: '0xb33EaAd8d922B1083446DC23f610c2567fB5180f',
      42161: '0xFa7F8980b0f1E64A2062791cc3b0871572f1F7f0',
      10: '0x6fd9d7AD17242c41f7131d257212c54A0e816691',
    },
  },
  {
    symbol: 'AAVE', name: 'Aave', decimals: 18, icon: '👻',
    addresses: {
      1: '0x7Fc66500c84A76Ad7e9c93437bFc5Ac33E2DDaE9',
      137: '0xD6DF932A45C0f255f85145f286eA0b292B21C90B',
      42161: '0xba5DdD1f9d7F570dc94a51479a000E3BCE967196',
      10: '0x76FB31fb4af56892A25e32cFC43De717950c9278',
      43114: '0x63a72806098Bd3D9520cC43356dD78afe5D386D9',
    },
  },
  {
    symbol: 'MKR', name: 'Maker', decimals: 18, icon: '♦',
    addresses: {
      1: '0x9f8F72aA9304c8B593d555F12eF6589cC3A579A2',
    },
  },
  {
    symbol: 'SNX', name: 'Synthetix', decimals: 18, icon: '◆',
    addresses: {
      1: '0xC011a73ee8576Fb46F5E1c5751cA3B9Fe0af2a6F',
      137: '0x50B728D8D964fd00C2d0AAD81718b71311feF68a',
      42161: '0xcBA56Cd8216FCBBF3fA6DF6137F3147cBcA37D60',
      10: '0x8700dAec35aF8Ff88c16BdF0418774CB3D7599B4',
    },
  },
  {
    symbol: 'CRV', name: 'Curve DAO', decimals: 18, icon: '◠',
    addresses: {
      1: '0xD533a949740bb3306d119CC777fa900bA034cd52',
      137: '0x172370d5Cd63279eFa6d502DAB29171933a610AF',
      42161: '0x11cDb42B0EB46D95f990BeDD4695A6e3fA034978',
    },
  },
  {
    symbol: 'COMP', name: 'Compound', decimals: 18, icon: '◉',
    addresses: {
      1: '0xc00e94Cb662C3520282E6f5717214004A7f26888',
      137: '0x8505b9d2254A7Ae468c0E9dd10Ccea3A837aef5c',
      42161: '0x354A6dA3fcde098F8389cad84b0182725c6C91dE',
    },
  },
  {
    symbol: 'LDO', name: 'Lido DAO', decimals: 18, icon: '🔷',
    addresses: {
      1: '0x5A98FcBEA516Cf06857215779Fd812CA3beF1B32',
      137: '0xC3C7d422809852031b44ab29EEC9F1EfF2A58756',
      42161: '0x13Ad51ed4F1B7e9Dc168d8a00cB3f4dDD85EfA60',
    },
  },
  {
    symbol: 'STETH', name: 'Lido Staked Ether', decimals: 18, icon: 'Ξ',
    addresses: {
      1: '0xae7ab96520DE3A18E5e111B5EaAb095312D7fE84',
    },
  },
  {
    symbol: 'WSTETH', name: 'Wrapped stETH', decimals: 18, icon: 'Ξ',
    addresses: {
      1: '0x7f39C581F595B53c5cb19bD0b3f8dA6c935E2Ca0',
      137: '0x03b54A6e9a984069379fae1a4fC4dBAE93B3bCCD',
      42161: '0x5979D7b546E38E414F7E9822514be443A4800529',
      10: '0x1F32b1c2345538c0c6f582fCB022739c4A194Ebb',
      8453: '0xc1CBa3fCea344f92D9239c08C0568f6F2F0ee452',
    },
  },
  {
    symbol: 'RPL', name: 'Rocket Pool', decimals: 18, icon: '🚀',
    addresses: {
      1: '0xD33526068D116cE69F19A9ee46F0bd304F21A51f',
      42161: '0xB766039cc6DB368759C1E56B79AFfE831546471D',
    },
  },
  {
    symbol: 'MATIC', name: 'Polygon (ERC20)', decimals: 18, icon: '⬡',
    addresses: {
      1: '0x7D1AfA7B718fb893dB30A3aBc0Cfc608AaCfeBB0',
    },
  },
  {
    symbol: 'ARB', name: 'Arbitrum', decimals: 18, icon: '◇',
    addresses: {
      42161: '0x912CE59144191C1204E64559FE8253a0e49E6548',
      1: '0xB50721BCf8d664c30412Cfbc6cf7a15145234ad1',
    },
  },
  {
    symbol: 'OP', name: 'Optimism', decimals: 18, icon: '◉',
    addresses: {
      10: '0x4200000000000000000000000000000000000042',
    },
  },
  {
    symbol: 'GRT', name: 'The Graph', decimals: 18, icon: '◈',
    addresses: {
      1: '0xc944E90C64B2c07662A292be6244BDf05Cda44a7',
      42161: '0x9623063377AD1B27544C965cCd7342f7EA7e88C7',
    },
  },

  // ═══════════════════════ DeFi / DEX ═══════════════════════
  {
    symbol: 'SUSHI', name: 'SushiSwap', decimals: 18, icon: '🍣',
    addresses: {
      1: '0x6B3595068778DD592e39A122f4f5a5cF09C90fE2',
      137: '0x0b3F868E0BE5597D5DB7fEB59E1CADBb0fdDa50a',
      42161: '0xd4d42F0b6DEF4CE0383636770eF773390d85c61A',
    },
  },
  {
    symbol: '1INCH', name: '1inch', decimals: 18, icon: '🦄',
    addresses: {
      1: '0x111111111117dC0aa78b770fA6A738034120C302',
      56: '0x111111111117dC0aa78b770fA6A738034120C302',
      137: '0x9c2C5fd7b07E95EE044DDeba0E97a665F142394f',
      42161: '0x6314C31A7a1652cE482cffe247E9CB7c3f4BB9aF',
    },
  },
  {
    symbol: 'DYDX', name: 'dYdX', decimals: 18, icon: '◆',
    addresses: {
      1: '0x92D6C1e31e14520e676a687F0a93788B716BEff5',
    },
  },
  {
    symbol: 'BAL', name: 'Balancer', decimals: 18, icon: '⊕',
    addresses: {
      1: '0xba100000625a3754423978a60c9317c58a424e3D',
      137: '0x9a71012B13CA4d3D0Cdc72A177DF3ef03b0E76A3',
      42161: '0x040d1EdC9569d4Bab2D15287Dc5A4F10F56a56B8',
    },
  },
  {
    symbol: 'GMX', name: 'GMX', decimals: 18, icon: '◈',
    addresses: {
      42161: '0xfc5A1A6EB076a2C7aD06eD22C90d7E710E35ad0a',
      43114: '0x62edc0692BD897D2295872a9FFCac5425011c661',
    },
  },
  {
    symbol: 'PENDLE', name: 'Pendle', decimals: 18, icon: '◇',
    addresses: {
      1: '0x808507121B80c02388fAd14726482e061B8da827',
      42161: '0x0c880f6761F1af8d9Aa9C466984b80DAb9a8c9e8',
    },
  },

  // ═══════════════════════ Meme Coins ═══════════════════════
  {
    symbol: 'SHIB', name: 'Shiba Inu', decimals: 18, icon: '🐕',
    addresses: {
      1: '0x95aD61b0a150d79219dCF64E1E6Cc01f0B64C4cE',
    },
  },
  {
    symbol: 'PEPE', name: 'Pepe', decimals: 18, icon: '🐸',
    addresses: {
      1: '0x6982508145454Ce325dDbE47a25d4ec3d2311933',
    },
  },
  {
    symbol: 'FLOKI', name: 'Floki Inu', decimals: 9, icon: '🐕',
    addresses: {
      1: '0xcf0C122c6b73ff809C693DB761e7BaeBe62b6a2E',
      56: '0xfb5B838b6cfEEdC2873aB27866079AC55363D37E',
    },
  },
  {
    symbol: 'BONK', name: 'Bonk (Wrapped)', decimals: 5, icon: '🐕',
    addresses: {
      1: '0x1151CB3d861920e07a38e03eEAd12C32178567F6',
    },
  },
  {
    symbol: 'WIF', name: 'dogwifhat (Wrapped)', decimals: 6, icon: '🐕',
    addresses: {
      1: '0xd21C7702CB26acFe1b37D1A6c7DB216abA472B43',
    },
  },

  // ═══════════════════════ Layer 2 / Infra ═══════════════════════
  {
    symbol: 'IMX', name: 'Immutable X', decimals: 18, icon: '◆',
    addresses: {
      1: '0xF57e7e7C23978C3cAEC3C3548E3D615c346e79fF',
    },
  },
  {
    symbol: 'STRK', name: 'Starknet', decimals: 18, icon: '◈',
    addresses: {
      1: '0xCa14007Eff0dB1f8135f4C25B34De49AB0d42766',
    },
  },
  {
    symbol: 'MANTA', name: 'Manta Network', decimals: 18, icon: '🔷',
    addresses: {
      1: '0x95CeF13441Be50d20cA4558CC0a27B601aC544E5',
    },
  },
  {
    symbol: 'ZK', name: 'zkSync', decimals: 18, icon: '◇',
    addresses: {
      1: '0x5A7d6b2F92C77FAD6CCaBd7EE0624E64907Eaf3E',
    },
  },

  // ═══════════════════════ Gaming / Metaverse ═══════════════════════
  {
    symbol: 'SAND', name: 'The Sandbox', decimals: 18, icon: '🎮',
    addresses: {
      1: '0x3845badAde8e6dFF049820680d1F14bD3903a5d0',
      137: '0xBbba073C31bF03b8ACf7c28EF0738DeCF3695683',
    },
  },
  {
    symbol: 'MANA', name: 'Decentraland', decimals: 18, icon: '🌐',
    addresses: {
      1: '0x0F5D2fB29fb7d3CFeE444a200298f468908cC942',
      137: '0xA1c57f48F0Deb89f569dFbE6E2B7f46D33606fD4',
    },
  },
  {
    symbol: 'AXS', name: 'Axie Infinity', decimals: 18, icon: '🎮',
    addresses: {
      1: '0xBB0E17EF65F82Ab018d8EDd776e8DD940327B28b',
    },
  },
  {
    symbol: 'GALA', name: 'Gala', decimals: 8, icon: '🎮',
    addresses: {
      1: '0xd1d2Eb1B1e90B638588728b4130137D262C87cae',
    },
  },
  {
    symbol: 'ENJ', name: 'Enjin Coin', decimals: 18, icon: '🎮',
    addresses: {
      1: '0xF629cBd94d3791C9250152BD8dfBDF380E2a3B9c',
    },
  },
  {
    symbol: 'ILV', name: 'Illuvium', decimals: 18, icon: '🎮',
    addresses: {
      1: '0x767FE9EDC9E0dF98E07454847909b5E959D7ca0E',
    },
  },

  // ═══════════════════════ AI Tokens ═══════════════════════
  {
    symbol: 'FET', name: 'Fetch.ai', decimals: 18, icon: '🤖',
    addresses: {
      1: '0xaea46A60368A7bD060eec7DF8CBa43b7EF41Ad85',
      56: '0x031b41e504677879370e9DBcF937283A8691Fa7f',
    },
  },
  {
    symbol: 'RNDR', name: 'Render Token', decimals: 18, icon: '🖥',
    addresses: {
      1: '0x6De037ef9aD2725EB40118Bb1702EBb27e4Aeb24',
      137: '0x61299774020dA444Af134c82fa83E3810b309991',
    },
  },
  {
    symbol: 'OCEAN', name: 'Ocean Protocol', decimals: 18, icon: '🌊',
    addresses: {
      1: '0x967da4048cD07aB37855c090aAF366e4ce1b9F48',
      137: '0x282d8efCe846A88B159800bd4130ad77443Fa1A1',
    },
  },
  {
    symbol: 'AGIX', name: 'SingularityNET', decimals: 8, icon: '🤖',
    addresses: {
      1: '0x5B7533812759B45C2B44C19e320ba2cD2681b542',
    },
  },
  {
    symbol: 'WLD', name: 'Worldcoin', decimals: 18, icon: '🌍',
    addresses: {
      1: '0x163f8C2467924be0ae7B5347228CABF260318753',
      10: '0xdC6fF44d5d932Cbd77B52E5612Ba0529DC6226F1',
    },
  },

  // ═══════════════════════ Oracle / Data ═══════════════════════
  {
    symbol: 'BAND', name: 'Band Protocol', decimals: 18, icon: '📊',
    addresses: {
      1: '0xBA11D00c5f74255f56a5E366F4F77f5A186d7f55',
    },
  },
  {
    symbol: 'API3', name: 'API3', decimals: 18, icon: '📡',
    addresses: {
      1: '0x0b38210ea11411557c13457D4dA7dC6ea731B88a',
    },
  },
  {
    symbol: 'PYTH', name: 'Pyth Network', decimals: 6, icon: '📈',
    addresses: {
      1: '0xeFc0CBceE29ee2e816b8C59eC50458C0F10F82A1',
    },
  },

  // ═══════════════════════ Privacy ═══════════════════════
  {
    symbol: 'ENS', name: 'Ethereum Name Service', decimals: 18, icon: '🔗',
    addresses: {
      1: '0xC18360217D8F7Ab5e7c516566761Ea12Ce7F9D72',
    },
  },

  // ═══════════════════════ Cross-chain / Bridge ═══════════════════════
  {
    symbol: 'RUNE', name: 'THORChain (ERC20)', decimals: 18, icon: '⚡',
    addresses: {
      1: '0x3155BA85D5F96b2d030a4966AF206230e46849cb',
    },
  },
  {
    symbol: 'STG', name: 'Stargate Finance', decimals: 18, icon: '✦',
    addresses: {
      1: '0xAf5191B0De278C7286d6C7CC6ab6BB8A73bA2Cd6',
      56: '0xB0D502E938ed5f4df2E681fE6E419ff29631d62b',
      137: '0x2F6F07CDcf3588944Bf4C42aC74ff24bF56e7590',
      42161: '0x6694340fc020c5E6B96567843da2df01b2CE1eb6',
      10: '0x296F55F8Fb28E498B858d0BcDA06D955B2Cb3f97',
      43114: '0x2F6F07CDcf3588944Bf4C42aC74ff24bF56e7590',
    },
  },
  {
    symbol: 'W', name: 'Wormhole', decimals: 18, icon: '🔮',
    addresses: {
      1: '0xB0fFa8000886e57F86dd5264b9582b2Ad87b2b91',
    },
  },

  // ═══════════════════════ Exchange Tokens ═══════════════════════
  {
    symbol: 'CRO', name: 'Cronos', decimals: 8, icon: '◈',
    addresses: {
      1: '0xA0b73E1Ff0B80914AB6fe0444E65848C4C34450b',
    },
  },
  {
    symbol: 'LEO', name: 'UNUS SED LEO', decimals: 18, icon: '🦁',
    addresses: {
      1: '0x2AF5D2aD76741191D15Dfe7bf6aC92d4Bd912Ca3',
    },
  },
  {
    symbol: 'OKB', name: 'OKB', decimals: 18, icon: '◉',
    addresses: {
      1: '0x75231F58b43240C9718Dd58B4967c5114342a86c',
    },
  },

  // ═══════════════════════ Storage / Compute ═══════════════════════
  {
    symbol: 'FIL', name: 'Filecoin (Wrapped)', decimals: 18, icon: '⨎',
    addresses: {
      1: '0x6e1A19F235bE7ED8E3369eF73b196C07257494DE',
      56: '0x0D8Ce2A99Bb6e3B7Db580eD848240e4a0F9aE153',
    },
  },
  {
    symbol: 'AR', name: 'Arweave (Wrapped)', decimals: 12, icon: '📦',
    addresses: {
      1: '0x4Fa9B813e665298cEebBC68A81c8fDA99D8Cd85F',
    },
  },

  // ═══════════════════════ Social / Identity ═══════════════════════
  {
    symbol: 'MASK', name: 'Mask Network', decimals: 18, icon: '🎭',
    addresses: {
      1: '0x69af81e73A73B40adF4f3d4223Cd9b1ECE623074',
      56: '0x2eD9a5C8C13b93955103B9a7C167B67Ef4d568a3',
      137: '0x2B9E7ccDF0F4e5B24757c1E1a80e311E34Cb10c7',
    },
  },
  {
    symbol: 'BLUR', name: 'Blur', decimals: 18, icon: '🌫',
    addresses: {
      1: '0x5283D291DBCF85356A21bA090E6db59121208b44',
    },
  },

  // ═══════════════════════ BSC Ecosystem ═══════════════════════
  {
    symbol: 'CAKE', name: 'PancakeSwap', decimals: 18, icon: '🥞',
    addresses: {
      56: '0x0E09FaBB73Bd3Ade0a17ECC321fD13a19e81cE82',
      1: '0x152649eA73beAb28c5b49B26eb48f7EAD6d4c0bA',
    },
  },
  {
    symbol: 'XVS', name: 'Venus', decimals: 18, icon: '♀',
    addresses: {
      56: '0xcF6BB5389c92Bdda8a3747Ddb454cB7a64626C63',
    },
  },
  {
    symbol: 'ALPACA', name: 'Alpaca Finance', decimals: 18, icon: '🦙',
    addresses: {
      56: '0x8F0528cE5eF7B51152A59745bEfDD91D97091d2F',
    },
  },

  // ═══════════════════════ Polygon Ecosystem ═══════════════════════
  {
    symbol: 'QUICK', name: 'QuickSwap', decimals: 18, icon: '⚡',
    addresses: {
      137: '0xB5C064F955D8e7F38fE0460C556a72987494eE17',
    },
  },

  // ═══════════════════════ Others ═══════════════════════
  {
    symbol: 'APE', name: 'ApeCoin', decimals: 18, icon: '🦍',
    addresses: {
      1: '0x4d224452801ACEd8B2F0aebE155379bb5D594381',
    },
  },
  {
    symbol: 'CHZ', name: 'Chiliz', decimals: 18, icon: '⚽',
    addresses: {
      1: '0x3506424F91fD33084466F402d5D97f05F8e3b4AF',
    },
  },
  {
    symbol: 'DENT', name: 'Dent', decimals: 8, icon: '📱',
    addresses: {
      1: '0x3597bfD533a99c9aa083587B074434E61Eb0A258',
    },
  },
  {
    symbol: 'HOT', name: 'Holo', decimals: 18, icon: '🔥',
    addresses: {
      1: '0x6c6EE5e31d828De241282B9606C8e98Ea48526E2',
    },
  },
  {
    symbol: 'JASMY', name: 'JasmyCoin', decimals: 18, icon: '🔷',
    addresses: {
      1: '0x7420B4b9a0110cdC71fB720908340C03F9Bc03EC',
    },
  },
  {
    symbol: 'LUNC', name: 'Terra Classic (Wrapped)', decimals: 6, icon: '🌙',
    addresses: {
      1: '0xd2877702675e6cEb975b4A1dFf9fb7BAF4C91ea9',
    },
  },
  {
    symbol: 'THETA', name: 'Theta (Wrapped)', decimals: 18, icon: 'Θ',
    addresses: {
      1: '0x3883f5e181fccaF8410FA61e12b59BAd963fb645',
    },
  },
  {
    symbol: 'ZIL', name: 'Zilliqa (Wrapped)', decimals: 12, icon: '◈',
    addresses: {
      1: '0x05f4a42e251f2d52b8ed15E9FEdAacFcEF1FAD27',
    },
  },
  {
    symbol: 'BAT', name: 'Basic Attention Token', decimals: 18, icon: '🦇',
    addresses: {
      1: '0x0D8775F648430679A709E98d2b0Cb6250d2887EF',
      137: '0x3Cef98bb43d732E2F285eE605a8158cDE967D219',
    },
  },
  {
    symbol: 'YFI', name: 'yearn.finance', decimals: 18, icon: '💠',
    addresses: {
      1: '0x0bc529c00C6401aEF6D220BE8C6Ea1667F6Ad93e',
      42161: '0x82e3A8F066a6989666b031d916c43672085b1582',
    },
  },
  {
    symbol: 'ZRX', name: '0x Protocol', decimals: 18, icon: '◈',
    addresses: {
      1: '0xE41d2489571d322189246DaFA5ebDe1F4699F498',
      137: '0x5559Edb74751A0edE9DeA4DC23aeE72cCA6bE3D5',
    },
  },
  {
    symbol: 'ANKR', name: 'Ankr', decimals: 18, icon: '⚓',
    addresses: {
      1: '0x8290333ceF9e6D528dD5618Fb97a76f268f3EDD4',
      56: '0xf307910A4c7bbc79691fD374889b36d8531B08e3',
    },
  },
  {
    symbol: 'CELO', name: 'Celo (Wrapped)', decimals: 18, icon: '🟡',
    addresses: {
      1: '0x3294395e62F4eB6aF3f1Fcf89f5602D90Fb3Ef69',
    },
  },
  {
    symbol: 'SKL', name: 'SKALE', decimals: 18, icon: '◈',
    addresses: {
      1: '0x00c83aeCC790e8a4453e5dD3B0B4b3680501a7A7',
    },
  },
  {
    symbol: 'STORJ', name: 'Storj', decimals: 8, icon: '☁',
    addresses: {
      1: '0xB64ef51C888972c908CFacf59B47C1AfBC0Ab8aC',
    },
  },
  {
    symbol: 'CELR', name: 'Celer Network', decimals: 18, icon: '🔷',
    addresses: {
      1: '0x4F9254C83EB525f9FCf346490bbb3ed28a81C667',
      56: '0x1f9f6a696C6Fd109cD3956F45dC709d2b3902163',
      42161: '0x3a8B787f78D775AECFEEa15706D4221B40F345AB',
    },
  },
  {
    symbol: 'ELF', name: 'aelf', decimals: 18, icon: '🧝',
    addresses: {
      1: '0xbf2179859fc6D5BEE9Bf9158632Dc51678a4100e',
    },
  },
  {
    symbol: 'RSR', name: 'Reserve Rights', decimals: 18, icon: '🔶',
    addresses: {
      1: '0x320623b8E4fF03373931769A31Fc52A4E78B5d70',
    },
  },
  {
    symbol: 'IOTX', name: 'IoTeX', decimals: 18, icon: '📡',
    addresses: {
      1: '0x6fB3e0A217407EFFf7Ca062D46c26E5d60a14d69',
    },
  },
  {
    symbol: 'RLC', name: 'iExec RLC', decimals: 9, icon: '💻',
    addresses: {
      1: '0x607F4C5BB672230e8672085532f7e901544a7375',
    },
  },
  {
    symbol: 'NMR', name: 'Numeraire', decimals: 18, icon: '🔢',
    addresses: {
      1: '0x1776e1F26f98b1A5dF9cD347953a26dd3Cb46671',
    },
  },
  {
    symbol: 'PAXG', name: 'PAX Gold', decimals: 18, icon: '🥇',
    addresses: {
      1: '0x45804880De22913dAFE09f4980848ECE6EcbAf78',
    },
  },
  {
    symbol: 'XAUt', name: 'Tether Gold', decimals: 6, icon: '🥇',
    addresses: {
      1: '0x68749665FF8D2d112Fa859AA293F07A622782F38',
    },
  },
];

// Get token info for a specific chain
export function getTokensForChain(chainId: number): (TokenInfo & { address: `0x${string}` })[] {
  return ERC20_TOKENS
    .filter(t => t.addresses[chainId])
    .map(t => ({ ...t, address: t.addresses[chainId] }));
}

// Get total number of unique tokens
export function getTotalTokenCount(): number {
  return ERC20_TOKENS.length;
}
